// useUserListings.jsx
import { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get, remove } from 'firebase/database';
import { useAuth } from './useAuth';

export const useUserListings = () => {
  const { currentUser } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);


  const refetch = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    if (!currentUser) {
      setError('User is not authenticated.');
      setLoading(false);
      return;
    }

    const fetchListings = async () => {
      const userId = currentUser.uid;

      try {
        const db = getDatabase();
        // Use users/{uid}/listings as source of truth - matches backend permission check
        // Supports owners, managers who received transfers, and any flow that populates this path
        const userListingsRef = ref(db, `users/${userId}/listings`);
        const userListingsSnap = await get(userListingsRef);

        if (!userListingsSnap.exists()) {
          setListings([]);
          return;
        }

        const listingIds = Object.keys(userListingsSnap.val()).filter(Boolean);
        if (listingIds.length === 0) {
          setListings([]);
          return;
        }

        // Fetch actual listing data for each ID
        const listingsDataRef = ref(db, 'listings');
        const listingsSnap = await get(listingsDataRef);
        const allListings = listingsSnap.exists() ? listingsSnap.val() : {};

        const userListings = listingIds
          .filter((propertyId) => allListings[propertyId])
          .map((propertyId) => ({
            propertyId,
            ...allListings[propertyId],
          }));

        setListings(userListings);
      } catch (error) {
        console.error('Error fetching listings:', error);
        setError('Failed to load listings');
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [tick, currentUser]);

  // Function to delete a listing
  const deleteListing = async (listingId) => {
    try {
      const db = getDatabase();
      const listingRef = ref(db, `listings/${listingId}`);

      // Remove the listing from Firebase
      await remove(listingRef);

      // Remove from the user's listings as well
      const userListingsRef = ref(db, `users/${currentUser.uid}/listings/${listingId}`);
      await remove(userListingsRef);

      // Update the state to reflect the deletion
      setListings((prevListings) => prevListings.filter((listing) => listing.propertyId !== listingId));
    } catch (error) {
      console.error('Error deleting listing:', error);
      setError('Failed to delete listing');
    }
  };

  return { listings, loading, error, deleteListing, refetch };
};
